import { useMemo } from 'react';

import type { InboundPromoCodesManager } from '@ctct/application-layer';
import type { InboundPromoCodesPageStore } from '@ctct/state-client-layer';
import { useCustomerInfo, usePartnerPromoCodesParamStore } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';

interface Props {
  initializer: { inboundPromoCodesInit: InboundPromoCodesManager['inboundPromoCodesInit'] | undefined };
  pageStore: InboundPromoCodesPageStore;
}

/**
 * Non-UI widget hook that captures inbound promo codes from URL params and partner IDs.
 * Should be called before usePromoCodesWidget so codes are stored before PromoCodesService runs.
 *
 * Note: If the initializer was created without inboundPromoCodes enabled, this hook will
 * throw an error indicating misconfiguration.
 */
export const useInboundPromoCodesWidget = ({ initializer, pageStore }: Props) => {
  const customer = useCustomerInfo();
  const { getPartnerIdToPromoCode } = usePartnerPromoCodesParamStore();

  const componentProps = useMemo(() => {
    if (!initializer.inboundPromoCodesInit) {
      throw new Error(
        'InboundPromoCodesManager not initialized. ' +
          'Pass { inboundPromoCodes: true } to PayflowInitializer constructor.'
      );
    }

    return initializer.inboundPromoCodesInit({
      pageStore,
      queries: [customer],
      paramStore: {
        getPartnerIdToPromoCode,
      },
    });
  }, [initializer, pageStore, customer, getPartnerIdToPromoCode]);

  useWidgetLifecycle(componentProps.status, componentProps.lifecycle);

  return null;
};
